import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { requestRecommendations } from "../../../utils/requestRecommendations";
import { storeLlmRecommendations, getStoredLlmRecommendations } from "../../../utils/recsCache";
import { getCategoryLabel } from "../../../utils/categoryLabels";
import { levelDisplayNames } from "../../../utils/displayNameUtils";
import { getRiskLevelColor, getRiskLevelPriorityColors } from "../../../utils/riskLevelUtils";
const FaIcon = ({ icon, color = "#3b82f6", size = 16 }) => (
  <i className={`fas ${icon}`} style={{ color, fontSize: size }} />
);

const SECTION_ICON_COLOR = "#3b82f6";
const SECTION_ICON_SIZE = 18;

const sectionCardShellStyle = {
  background: "linear-gradient(145deg, var(--card-bg), rgba(59,130,246,0.05))",
  borderRadius: "16px",
  border: "1px solid var(--card-border)",
  boxShadow: "0 12px 30px rgba(15,23,42,0.08)",
  maxWidth: "100%",
  overflowX: "hidden",
};

const SectionCardIcon = ({ icon }) => (
  <div
    style={{
      width: "40px",
      height: "40px",
      borderRadius: "12px",
      background: "rgba(59,130,246,0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "20px",
      color: "#3b82f6",
      flexShrink: 0,
    }}
  >
    {icon}
  </div>
);

const SectionCard = ({ children, title, icon }) => (
  <div
    style={{
      ...sectionCardShellStyle,
      padding: "24px",
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <SectionCardIcon icon={icon} />
      <h3 style={{ margin: 0, color: "var(--card-text)", fontSize: "18px" }}>{title}</h3>
    </div>
    {children}
  </div>
);

const SectionCardAccordion = ({ children, title, icon, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div style={{ ...sectionCardShellStyle, overflow: "hidden" }}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "24px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
          <SectionCardIcon icon={icon} />
          <h3 style={{ margin: 0, color: "var(--card-text)", fontSize: "18px" }}>{title}</h3>
        </div>
        <i
          className={`fas fa-chevron-${isOpen ? "up" : "down"}`}
          style={{ color: "#64748b", fontSize: "14px", flexShrink: 0 }}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            padding: "0 24px 24px",
            maxWidth: "100%",
            overflowX: "hidden",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const codeSnippetStyle = {
  background: "rgba(107, 114, 128, 0.05)",
  padding: "16px",
  borderRadius: "12px",
  overflow: "hidden",
  margin: 0,
  fontSize: "12px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  fontFamily: "monospace",
  color: "var(--body-text)",
  lineHeight: 1.6,
  border: "1px solid rgba(107, 114, 128, 0.1)",
};

const codeCompactStyle = {
  background: "rgba(107, 114, 128, 0.05)",
  padding: "10px",
  borderRadius: "6px",
  margin: 0,
  fontSize: "11px",
  fontFamily: "monospace",
  color: "var(--body-text)",
  overflow: "hidden",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  overflowWrap: "break-word",
  lineHeight: 1.5,
};

const formatCategoryName = (name) => {
  return getCategoryLabel(name);
};

const formatPostureDisplay = (value) => {
  if (!value) return value;
  return value.replace(/_/g, " ");
};

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getRiskLevelStyle = (riskLevel) => {
  const textColor = getRiskLevelColor(riskLevel);
  return {
    bg: hexToRgba(textColor, 0.06),
    border: hexToRgba(textColor, 0.15),
    text: textColor,
  };
};

const StatChip = ({ label, value, color = "#3b82f6", capitalize = false }) => {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div style={{
      background: hexToRgba(color, 0.06),
      borderRadius: "10px",
      padding: "14px",
      border: `1px solid ${hexToRgba(color, 0.15)}`
    }}>
      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{
        fontSize: "16px",
        fontWeight: 700,
        color,
        textTransform: capitalize ? "capitalize" : "none"
      }}>
        {value}
      </div>
    </div>
  );
};

const chipStyle = {
  padding: "6px 12px",
  borderRadius: "8px",
  fontSize: "12px",
  fontWeight: 600,
  color: "#3b82f6",
  background: "rgba(59, 130, 246, 0.1)",
  border: "1px solid rgba(59, 130, 246, 0.15)"
};

const subBoxStyle = {
  background: "rgba(107, 114, 128, 0.05)",
  padding: "16px",
  borderRadius: "8px",
  border: "1px solid rgba(107, 114, 128, 0.1)"
};

const CategorizedAnalysisRecsAndPrioritizedActionsCard = ({ item, index = 0, defaultOpen = false }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "recs.aiModel" });
  const [isOpen, setIsOpen] = useState(defaultOpen);
  if (!item) return null;

  const priority = item.priority?.toLowerCase() || "medium";
  const style = getRiskLevelPriorityColors(priority);
  const evidence = item.evidence;

  const textBox = (label, value) => (
    <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "8px", height: "100%" }}>
      <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ ...subBoxStyle, color: "var(--body-text)", fontSize: "13px", lineHeight: 1.6, flex: 1 }}>
        {value}
      </div>
    </div>
  );

  const kvBox = (entries) => (
    <div style={{
      ...subBoxStyle,
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      flex: 1
    }}>
      {entries.map(([key, value]) => (
        <div key={key} style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "16px",
          paddingBottom: "10px",
          borderBottom: "1px solid rgba(107, 114, 128, 0.1)"
        }}>
          <strong style={{ color: "var(--card-text)", fontSize: "13px", fontWeight: 500 }}>
            {formatCategoryName(key)}
          </strong>
          <span style={{ color: "var(--body-text)", fontSize: "13px", textAlign: "right" }}>
            {value}
          </span>
        </div>
      ))}
    </div>
  );

  const hasFailureReasons = Array.isArray(evidence?.failure_reasons) && evidence.failure_reasons.length > 0;
  const hasFailedCaseIds = Array.isArray(evidence?.failed_case_ids) && evidence.failed_case_ids.length > 0;
  const hasStatusCounts = evidence?.status_counts && typeof evidence.status_counts === "object" && Object.keys(evidence.status_counts).length > 0;
  const hasLatency = evidence?.latency_ms && typeof evidence.latency_ms === "object" && Object.keys(evidence.latency_ms).length > 0;
  const hasObservedOutcomes = evidence?.observed_outcomes && typeof evidence.observed_outcomes === "object" && Object.keys(evidence.observed_outcomes).length > 0;
  const hasEvidence = hasFailureReasons || hasFailedCaseIds || hasStatusCounts || hasLatency || hasObservedOutcomes;

  return (
    <div style={{
      border: "1px solid var(--card-border)",
      borderRadius: "14px",
      overflow: "hidden",
      background: "var(--card-bg)"
    }}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        style={{
          width: "100%",
          padding: "16px 20px",
          background: "rgba(107, 114, 128, 0.04)",
          border: "none",
          borderBottom: isOpen ? "1px solid var(--card-border)" : "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 0 }}>
          <div style={{
            width: "32px",
            height: "32px",
            borderRadius: "8px",
            background: "rgba(107, 114, 128, 0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: "14px",
            fontWeight: 700,
            color: "var(--card-text)"
          }}>
            {index + 1}
          </div>
          {item.action && (
            <div style={{ fontSize: "17px", fontWeight: 600, color: "var(--card-text)" }}>
              {item.action}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
          {item.id && (
            <span style={{
              padding: "5px 12px",
              borderRadius: "999px",
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.05em",
              color: "var(--card-text)",
              background: "rgba(107, 114, 128, 0.08)",
              border: "1px solid rgba(107, 114, 128, 0.15)",
              whiteSpace: "nowrap"
            }}>
              {item.id}
            </span>
          )}
          <span style={{
            padding: "5px 12px",
            borderRadius: "999px",
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "0.05em",
            color: "#fff",
            background: style.text,
            border: `1px solid ${style.border}`,
            whiteSpace: "nowrap"
          }}>
            {levelDisplayNames[item.priority] || item.priority || t("unknown")}
          </span>
          <i
            className={`fas fa-chevron-${isOpen ? "up" : "down"}`}
            style={{ color: "#64748b", fontSize: "14px", flexShrink: 0 }}
            aria-hidden="true"
          />
        </div>
      </button>

      {isOpen && (
        <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "12px" }}>
          {(item.description || item.acceptance_test) && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px", alignItems: "stretch" }}>
              {item.description && textBox(t("description"), item.description)}
              {item.acceptance_test && textBox(t("acceptanceTest"), item.acceptance_test)}
            </div>
          )}

          {item.implementation && Array.isArray(item.implementation) && item.implementation.length > 0 && (
            <div>
              <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px", textTransform: "uppercase" }}>
                {t("implementation")}

              </div>
              <div style={subBoxStyle}>
                <ul style={{ margin: 0, paddingLeft: "20px", color: "var(--body-text)", fontSize: "13px", lineHeight: 1.8 }}>
                  {item.implementation.map((step, stepIdx) => (
                    <li key={stepIdx}>{step}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {item.control_families && Array.isArray(item.control_families) && item.control_families.length > 0 && (
            <div>
              <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px", textTransform: "uppercase" }}>
                {t("controlFamilies")}

              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {item.control_families.map((family, familyIdx) => (
                  <span key={familyIdx} style={chipStyle}>{family}</span>
                ))}
              </div>
            </div>
          )}

          {item.python_packages_or_modules && Array.isArray(item.python_packages_or_modules) && item.python_packages_or_modules.length > 0 && (
            <div>
              <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px", textTransform: "uppercase" }}>
                {t("pythonPackagesOrModules")}

              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {item.python_packages_or_modules.map((pkg, pkgIdx) => (
                  <span key={pkgIdx} style={chipStyle}>{pkg}</span>
                ))}
              </div>
            </div>
          )}

          {hasEvidence && (
            <div>
              <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px", textTransform: "uppercase" }}>
                {t("evidence")}

              </div>
              <div style={{ ...subBoxStyle, display: "flex", flexDirection: "column", gap: "16px" }}>
                {hasFailureReasons && (
                  <div>
                    <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px", textTransform: "uppercase" }}>
                      {t("failureReasons")}

                    </div>
                    <div style={subBoxStyle}>
                      <ul style={{ margin: 0, paddingLeft: "20px", color: "var(--body-text)", fontSize: "13px", lineHeight: 1.8 }}>
                        {evidence.failure_reasons.map((reason, reasonIdx) => (
                          <li key={reasonIdx}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {hasFailedCaseIds && (
                  <div>
                    <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px", textTransform: "uppercase" }}>
                      {t("failedCaseIds")}

                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {evidence.failed_case_ids.map((caseId, caseIdx) => (
                        <span key={caseIdx} style={chipStyle}>{caseId}</span>
                      ))}
                    </div>
                  </div>
                )}

                {(hasStatusCounts || hasLatency || hasObservedOutcomes) && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "16px", alignItems: "stretch" }}>
                    {hasStatusCounts && (
                      <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "8px", height: "100%" }}>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", textTransform: "uppercase" }}>
                          {t("statusCounts")}

                        </div>
                        {kvBox(Object.entries(evidence.status_counts))}
                      </div>
                    )}
                    {hasLatency && (
                      <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "8px", height: "100%" }}>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", textTransform: "uppercase" }}>
                          {t("latencyMs")}

                        </div>
                        {kvBox(Object.entries(evidence.latency_ms))}
                      </div>
                    )}
                    {hasObservedOutcomes && (
                      <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "8px", height: "100%" }}>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", textTransform: "uppercase" }}>
                          {t("observedOutcomes")}

                        </div>
                        {kvBox(Object.entries(evidence.observed_outcomes))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {item.third_party_options && Array.isArray(item.third_party_options) && item.third_party_options.length > 0 && (
            <div>
              <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px", textTransform: "uppercase" }}>
                {t("thirdPartyOptions")}

              </div>
              <div style={{ overflowX: "auto", border: "1px solid var(--card-border)", borderRadius: "8px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ background: "rgba(107, 114, 128, 0.06)" }}>
                      <th style={{ textAlign: "left", padding: "10px 12px", color: "var(--muted-text)", fontWeight: 600, textTransform: "uppercase", fontSize: "11px", borderBottom: "1px solid var(--card-border)" }}>{t("vendor")}</th>
                      <th style={{ textAlign: "left", padding: "10px 12px", color: "var(--muted-text)", fontWeight: 600, textTransform: "uppercase", fontSize: "11px", borderBottom: "1px solid var(--card-border)" }}>{t("fit")}</th>
                      <th style={{ textAlign: "left", padding: "10px 12px", color: "var(--muted-text)", fontWeight: 600, textTransform: "uppercase", fontSize: "11px", borderBottom: "1px solid var(--card-border)" }}>{t("url")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {item.third_party_options.map((option, optionIdx) => (
                      <tr key={optionIdx} style={{ borderBottom: optionIdx < item.third_party_options.length - 1 ? "1px solid var(--card-border)" : "none" }}>
                        <td style={{ padding: "12px", color: "var(--card-text)", fontWeight: 600, verticalAlign: "top" }}>
                          {option.vendor}
                        </td>
                        <td style={{ padding: "12px", color: "var(--body-text)", lineHeight: 1.5, verticalAlign: "top" }}>
                          {option.fit}
                        </td>
                        <td style={{ padding: "12px", verticalAlign: "top", maxWidth: "280px", overflowWrap: "anywhere", wordBreak: "break-word" }}>
                          {option.url ? (
                            <a href={option.url} target="_blank" rel="noopener noreferrer" style={{ color: "#3b82f6", textDecoration: "none" }}>
                              {option.url}
                            </a>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AiModelAttackSurfaceRecs = ({ reportData }) => {
  const { t } = useTranslation("reports_recs", { keyPrefix: "recs.aiModel" });
  const [recommendationDetails, setRecommendationDetails] = useState(() => {
    const reportId = reportData?.id;
    return reportId ? getStoredLlmRecommendations(reportId) : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const reportId = reportData?.id;
    if (!reportId) {
      setRecommendationDetails(null);
      return;
    }

    const stored = getStoredLlmRecommendations(reportId);
    if (stored) {
      setRecommendationDetails(stored);
    } else {
      setRecommendationDetails(null);
    }
  }, [reportData?.id]);

  const fetchRecommendations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get report ID from reportData
      const reportId = reportData?.id;
      if (!reportId) {
        throw new Error(t("reportIdNotFound"));
      }

      const result = await requestRecommendations({
        recommFor: "llm",
        reportId,
        reportData,
        fallbackMessage: t("noRecommendationsAvailable"),
      });
      setRecommendationDetails(result);

      if (result && Object.keys(result).length > 0) {
        storeLlmRecommendations(reportId, result);
      }
    } catch (err) {
      console.error("Error fetching recommendations:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!recommendationDetails && !loading && !error) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "60px 40px",
          background: "var(--card-bg)",
          borderRadius: "12px",
          border: "1px solid var(--card-border)",
          marginTop: "20px",
        }}
      >
        <div
          style={{
            width: "64px",
            height: "64px",
            borderRadius: "16px",
            background: "rgba(59, 130, 246, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "20px",
          }}
        >
          <i className="fas fa-lightbulb" style={{ fontSize: "28px", color: "#3b82f6" }} />
        </div>

        <button
          type="button"
          onClick={fetchRecommendations}
          disabled={loading}
          style={{
            padding: "12px 32px",
            background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
            opacity: loading ? 0.6 : 1,
          }}
        >
          <i className="fas fa-download" style={{ marginRight: "8px" }} />
          {t("loadRecommendations")}

        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "60px 40px",
          background: "var(--card-bg)",
          borderRadius: "12px",
          border: "1px solid var(--card-border)",
          marginTop: "20px",
        }}
      >
        <i className="fas fa-spinner fa-spin" style={{ fontSize: "32px", color: "#3b82f6", marginBottom: "16px" }} />
        <div style={{ fontSize: "16px", color: "var(--card-text)" }}>{t("loadingRecommendations")}</div>
      </div>
    );
  }

  if (error && !recommendationDetails) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "60px 40px",
          background: "var(--card-bg)",
          borderRadius: "12px",
          border: "1px solid var(--card-border)",
          marginTop: "20px",
          gap: "16px",
        }}
      >
        <div style={{ color: "#ef4444", fontWeight: 600 }}>{error}</div>
        <button
          type="button"
          onClick={fetchRecommendations}
          style={{
            padding: "10px 20px",
            background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          {t("retry")}

        </button>
      </div>
    );
  }

  if (recommendationDetails && Object.keys(recommendationDetails).length === 0) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 40px",
        background: "var(--card-bg)",
        borderRadius: "12px",
        border: "1px solid var(--card-border)",
        marginTop: "20px"
      }}>
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "16px",
          background: "rgba(107, 114, 128, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px"
        }}>
          <i className="fas fa-compass" style={{ fontSize: "28px", color: "#6b7280" }}></i>
        </div>
        <div style={{ fontSize: "18px", color: "var(--card-text)", fontWeight: 600 }}>
          {t("noRecommendationsAvailable")}

        </div>
      </div>
    );
  }

  const recommendations = recommendationDetails;

  if (!recommendations) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <div style={{ fontSize: "16px", color: "var(--body-text)" }}>
          {t("noRecommendationsAvailable")}

        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px", padding: "10px 0 40px", maxWidth: "100%", overflowX: "hidden" }}>
      {/* Section 1: Executive Summary */}
      {recommendations.executive_summary && (
        <SectionCard title={t("executiveSummary")} icon={<FaIcon icon="fa-chart-bar" color={SECTION_ICON_COLOR} size={SECTION_ICON_SIZE} />}>
          {recommendations.executive_summary.security_status && (() => {
            const status = recommendations.executive_summary.security_status;
            const displayStatus = levelDisplayNames[status] || status;
            const statusStyle = getRiskLevelPriorityColors(status);

            return (
              <div style={{
                background: statusStyle.gradient,
                borderRadius: "12px",
                padding: "28px",
                color: "white",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: "14px"
              }}>
                <div style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  opacity: 0.9
                }}>
                  {t("overallRisk")}

                </div>
                <div style={{
                  fontSize: "11px",
                  fontWeight: 500,
                  opacity: 0.8,
                  marginTop: "-6px"
                }}>
                  {t("basedOnGeneratedPrompts")}
                </div>
                <div style={{
                  fontSize: "36px",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                }}>
                  {displayStatus}
                </div>
                {recommendations.executive_summary.recommended_timeline && (
                  <div style={{
                    padding: "8px 20px",
                    background: "rgba(255,255,255,0.2)",
                    borderRadius: "999px",
                    fontSize: "14px",
                    fontWeight: 500
                  }}>
                    {recommendations.executive_summary.recommended_timeline}
                  </div>
                )}
              </div>
            );
          })()}

          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px"
          }}>
            {/* Key Findings from Executive Summary */}
            {recommendations.executive_summary.key_findings && Array.isArray(recommendations.executive_summary.key_findings) && recommendations.executive_summary.key_findings.length > 0 && (
              <div style={{
                background: "linear-gradient(145deg, var(--card-bg), rgba(59, 130, 246, 0.05))",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid var(--card-border)"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                  <FaIcon icon="fa-info-circle" color="#3b82f6" size={16} />
                  <h5 style={{ margin: 0, fontSize: "16px", fontWeight: "600", color: "var(--card-text)" }}>{t("keyFindings")}</h5>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {recommendations.executive_summary.key_findings.map((finding, index) => (
                    <div key={index} style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                      padding: "14px 16px",
                      background: "rgba(59, 130, 246, 0.06)",
                      border: "1px solid rgba(59, 130, 246, 0.15)",
                      borderRadius: "10px"
                    }}>
                      <div style={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "6px",
                        background: "rgba(59, 130, 246, 0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                      }}>
                        <FaIcon icon="fa-info-circle" color="#3b82f6" size={12} />
                      </div>
                      <div style={{ color: "var(--card-text)", lineHeight: 1.6, fontSize: "14px" }}>
                        {finding}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Overall Assessment inside Executive Summary */}
            {recommendations.overall_assessment && (
              <div style={{
                background: "linear-gradient(145deg, var(--card-bg), rgba(139, 92, 246, 0.05))",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid var(--card-border)"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                  <FaIcon icon="fa-bullseye" color="#8b5cf6" size={16} />
                  <h5 style={{ margin: 0, fontSize: "16px", fontWeight: "600", color: "var(--card-text)" }}>{t("overallAssessment")}</h5>
                </div>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                  gap: "12px"
                }}>
                  {recommendations.overall_assessment.security_posture && (() => {
                    const posture = recommendations.overall_assessment.security_posture;
                    const postureStyle = getRiskLevelStyle(posture);

                    return (
                      <div style={{
                        background: postureStyle.bg,
                        borderRadius: "12px",
                        padding: "16px",
                        border: `1px solid ${postureStyle.border}`,
                        textAlign: "center"
                      }}>
                        <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          {t("securityPosture")}

                        </div>
                        <div style={{ fontSize: "22px", fontWeight: 700, color: postureStyle.text }}>
                          {levelDisplayNames[posture] || t("unknown")}
                        </div>
                      </div>
                    );
                  })()}
                  {recommendations.overall_assessment.remediation_timeline && (
                    <div style={{
                      background: "rgba(245, 158, 11, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(245, 158, 11, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("timeline")}

                      </div>
                      <div style={{ fontSize: "14px", fontWeight: 600, color: "#f59e0b" }}>
                        {recommendations.overall_assessment.remediation_timeline}
                      </div>
                    </div>
                  )}
                  {recommendations.overall_assessment.total_tests !== undefined && (
                    <div style={{
                      background: "rgba(59, 130, 246, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(59, 130, 246, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("totalTests")}

                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#3b82f6" }}>
                        {recommendations.overall_assessment.total_tests}
                      </div>
                    </div>
                  )}
                  {recommendations.overall_assessment.total_passed_tests !== undefined && (
                    <div style={{
                      background: "rgba(16, 185, 129, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(16, 185, 129, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("passed")}

                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#10b981" }}>
                        {recommendations.overall_assessment.total_passed_tests}
                      </div>
                    </div>
                  )}
                  {recommendations.overall_assessment.total_failed_tests !== undefined && (
                    <div style={{
                      background: "rgba(239, 68, 68, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(239, 68, 68, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("failedToBlock")}

                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#ef4444" }}>
                        {recommendations.overall_assessment.total_failed_tests}
                      </div>
                    </div>
                  )}
                  {recommendations.overall_assessment.critical_vulnerabilities !== undefined && (
                    <div style={{
                      background: "rgba(153, 27, 27, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(153, 27, 27, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("criticalVulnerabilities")}

                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#991b1b" }}>
                        {recommendations.overall_assessment.critical_vulnerabilities}
                      </div>
                    </div>
                  )}
                  {recommendations.overall_assessment.high_vulnerabilities !== undefined && (
                    <div style={{
                      background: "rgba(234, 88, 12, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(234, 88, 12, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("highVulnerabilities")}

                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#ea580c" }}>
                        {recommendations.overall_assessment.high_vulnerabilities}
                      </div>
                    </div>
                  )}
                  {recommendations.overall_assessment.overall_risk_score !== undefined && (
                    <div style={{
                      background: "rgba(239, 68, 68, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(239, 68, 68, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("overallRisk")}

                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#ef4444" }}>
                        {recommendations.overall_assessment.overall_risk_score.toFixed(2)}%
                      </div>
                    </div>
                  )}
                  {recommendations.overall_assessment.overall_readiness !== undefined && (
                    <div style={{
                      background: "rgba(34, 197, 94, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(34, 197, 94, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("overallReadiness")}

                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#22c55e" }}>
                        {recommendations.overall_assessment.overall_readiness.toFixed(2)}%
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quick Stats inside Executive Summary */}
            {recommendations.quick_stats && (
              <div style={{
                background: "linear-gradient(145deg, var(--card-bg), rgba(107, 114, 128, 0.05))",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid var(--card-border)"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                  <FaIcon icon="fa-chart-bar" color="#6b7280" size={16} />
                  <h5 style={{ margin: 0, fontSize: "16px", fontWeight: "600", color: "var(--card-text)" }}>{t("quickStats")}</h5>
                </div>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: "12px"
                }}>
                  {recommendations.quick_stats.total_tests !== undefined && (
                    <div style={{
                      background: "rgba(59, 130, 246, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(59, 130, 246, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("totalTests")}

                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "var(--card-text)" }}>
                        {recommendations.quick_stats.total_tests}
                      </div>
                    </div>
                  )}
                  {recommendations.quick_stats.total_failed_tests !== undefined && (
                    <div style={{
                      background: "rgba(239, 68, 68, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(239, 68, 68, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("failedToBlockLower")}

                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#ef4444" }}>
                        {recommendations.quick_stats.total_failed_tests}
                      </div>
                    </div>
                  )}
                  {recommendations.quick_stats.total_high_risk !== undefined && (
                    <div style={{
                      background: "rgba(234, 88, 12, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(234, 88, 12, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("highRisk")}

                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#ef4444" }}>
                        {recommendations.quick_stats.total_high_risk}
                      </div>
                    </div>
                  )}
                  {recommendations.quick_stats.overall_readiness !== undefined && (
                    <div style={{
                      background: "rgba(34, 197, 94, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(34, 197, 94, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("readiness")}

                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#22c55e" }}>
                        {recommendations.quick_stats.overall_readiness.toFixed(1)}%
                      </div>
                    </div>
                  )}
                  {recommendations.quick_stats.total_categories_tested !== undefined && (
                    <div style={{
                      background: "rgba(139, 92, 246, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(139, 92, 246, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("categories")}

                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#8b5cf6" }}>
                        {recommendations.quick_stats.total_categories_tested}
                      </div>
                    </div>
                  )}
                  {recommendations.quick_stats.overall_avg_response_time !== undefined && (
                    <div style={{
                      background: "rgba(245, 158, 11, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(245, 158, 11, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("avgResponseTime")}

                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#f59e0b" }}>
                        {recommendations.quick_stats.overall_avg_response_time.toFixed(3)}
                      </div>
                    </div>
                  )}
                  {recommendations.quick_stats.overall_risk_level && (() => {
                    const riskStyle = getRiskLevelStyle(recommendations.quick_stats.overall_risk_level);
                    return (
                      <div style={{
                        background: riskStyle.bg,
                        borderRadius: "12px",
                        padding: "16px",
                        border: `1px solid ${riskStyle.border}`,
                        textAlign: "center"
                      }}>
                        <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          {t("overallRiskLevel")}

                        </div>
                        <div style={{ fontSize: "22px", fontWeight: 700, color: riskStyle.text, textTransform: "capitalize" }}>
                          {levelDisplayNames[recommendations.quick_stats.overall_risk_level] || recommendations.quick_stats.overall_risk_level}
                        </div>
                      </div>
                    );
                  })()}
                  {recommendations.quick_stats.api_error_count?.llm_api_mis_use_abuse !== undefined && (
                    <div style={{
                      background: "rgba(100, 116, 139, 0.06)",
                      borderRadius: "12px",
                      padding: "16px",
                      border: "1px solid rgba(100, 116, 139, 0.15)",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        {t("apiErrorCount")}

                      </div>
                      <div style={{ fontSize: "22px", fontWeight: 700, color: "#64748b" }}>
                        {recommendations.quick_stats.api_error_count.llm_api_mis_use_abuse}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </SectionCard>
      )}


      {/* Section 2: Categorized Analysis */}
      {recommendations.categorized_analysis && Object.keys(recommendations.categorized_analysis).length > 0 && (
        <SectionCardAccordion title={t("categorizedAnalysis")} icon={<FaIcon icon="fa-file-alt" color={SECTION_ICON_COLOR} size={SECTION_ICON_SIZE} />}>
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {Object.entries(recommendations.categorized_analysis).map(([category, data]) => {
              const headerColor =
                data.status === "vulnerable" ? "#991b1b" :
                data.status === "passed" || data.status === "secure" ? "#065f46" :
                "#92400e";

              return (
              <div key={category} style={{
                border: "1px solid var(--card-border)",
                borderRadius: "12px",
                overflow: "hidden",
                background: "var(--card-bg)"
              }}>
                <div style={{
                  padding: "16px 20px",
                  background: headerColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px"
                }}>
                  <h4 style={{ margin: 0, fontSize: "16px", fontWeight: "600", color: "white", textTransform: "uppercase" }}>
                    {formatCategoryName(category)}
                  </h4>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {data.status && (
                      <span style={{
                        padding: "4px 12px",
                        borderRadius: "999px",
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        background: "rgba(255,255,255,0.2)",
                        color: "white"
                      }}>
                        {data.status}
                      </span>
                    )}
                    {data.risk_score !== undefined && (
                      <span style={{
                        padding: "4px 12px",
                        borderRadius: "999px",
                        fontSize: "11px",
                        fontWeight: 600,
                        background: "rgba(255,255,255,0.2)",
                        color: "white"
                      }}>
                        Risk: {Number.isInteger(data.risk_score) ? data.risk_score : data.risk_score.toFixed(1)}
                      </span>
                    )}
                    {data.subcategories_affected !== undefined && (
                      <span style={{
                        padding: "4px 12px",
                        borderRadius: "999px",
                        fontSize: "11px",
                        fontWeight: 600,
                        background: "rgba(255,255,255,0.2)",
                        color: "white"
                      }}>
                        {data.subcategories_affected} {t("subcategoriesAffectedChip")}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ padding: "20px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "12px", marginBottom: "16px" }}>
                  <StatChip label={t("criticalVulnerabilities")} value={data.critical_vulnerabilities !== undefined ? data.critical_vulnerabilities : null} color="#991b1b" />
                  <StatChip label={t("highVulnerabilities")} value={data.high_vulnerabilities !== undefined ? data.high_vulnerabilities : null} color="#ea580c" />
                  <StatChip label={t("totalTests")} value={data.total_tests !== undefined ? data.total_tests : null} color="#3b82f6" />
                  <StatChip label={t("totalPassed")} value={data.total_passed !== undefined ? data.total_passed : null} color="#22c55e" />
                  <StatChip label={t("failedToBlock")} value={data.total_failed !== undefined ? data.total_failed : null} color="#ef4444" />
                  <StatChip label={t("blocked")} value={data.blocked !== undefined ? data.blocked : null} color="#22c55e" />
                  <StatChip label={t("allowed")} value={data.allowed !== undefined ? data.allowed : null} color="#ef4444" />
                  <StatChip
                    label={t("readiness")}
                    value={data.readiness !== undefined ? `${Number.isInteger(data.readiness) ? data.readiness : data.readiness.toFixed(2)}%` : null}
                    color="#22c55e"
                  />
                </div>

                {((data.status_counts && typeof data.status_counts === "object" && Object.keys(data.status_counts).length > 0) ||
                  (data.latency_ms && typeof data.latency_ms === "object" && Object.keys(data.latency_ms).length > 0)) && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px", marginBottom: "16px", alignItems: "stretch" }}>
                    {data.status_counts && typeof data.status_counts === "object" && Object.keys(data.status_counts).length > 0 && (
                      <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px", height: "100%" }}>
                        <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                          {t("statusCounts")}

                        </div>
                        <div style={{
                          background: "rgba(107, 114, 128, 0.05)",
                          padding: "16px",
                          borderRadius: "8px",
                          border: "1px solid rgba(107, 114, 128, 0.1)",
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                          flex: 1
                        }}>
                          {Object.entries(data.status_counts).map(([code, count]) => (
                            <div key={code} style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              gap: "16px",
                              paddingBottom: "10px",
                              borderBottom: "1px solid rgba(107, 114, 128, 0.1)"
                            }}>
                              <strong style={{ color: "var(--card-text)", fontSize: "13px", fontWeight: 500 }}>
                                {code}
                              </strong>
                              <span style={{ color: "var(--body-text)", fontSize: "13px", textAlign: "right" }}>
                                {count}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {data.latency_ms && typeof data.latency_ms === "object" && Object.keys(data.latency_ms).length > 0 && (
                      <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px", height: "100%" }}>
                        <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                          {t("latencyMs")}

                        </div>
                        <div style={{
                          background: "rgba(107, 114, 128, 0.05)",
                          padding: "16px",
                          borderRadius: "8px",
                          border: "1px solid rgba(107, 114, 128, 0.1)",
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                          flex: 1
                        }}>
                          {Object.entries(data.latency_ms).map(([key, value]) => (
                            <div key={key} style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              gap: "16px",
                              paddingBottom: "10px",
                              borderBottom: "1px solid rgba(107, 114, 128, 0.1)"
                            }}>
                              <strong style={{ color: "var(--card-text)", fontSize: "13px", fontWeight: 500, textTransform: "capitalize" }}>
                                {formatCategoryName(key)}
                              </strong>
                              <span style={{ color: "var(--body-text)", fontSize: "13px", textAlign: "right" }}>
                                {value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {data.recommendations && Array.isArray(data.recommendations) && data.recommendations.length > 0 && (() => {
                  // Sort recommendations by priority: critical, high, medium, low
                  const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
                  const sortedRecs = [...data.recommendations].sort((a, b) => {
                    const aPriority = priorityOrder[a.priority?.toLowerCase()] ?? 99;
                    const bPriority = priorityOrder[b.priority?.toLowerCase()] ?? 99;
                    return aPriority - bPriority;
                  });

                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                        {t("recommendations")}

                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        {sortedRecs.map((rec, idx) => (
                          <CategorizedAnalysisRecsAndPrioritizedActionsCard key={rec.id || idx} item={rec} index={idx} />
                        ))}
                      </div>
                    </div>
                  );
                })()}
                </div>
              </div>
              );
            })}
          </div>
        </SectionCardAccordion>
      )}

      {/* Section 3: Prioritized Actions */}
      {recommendations.prioritized_actions && Array.isArray(recommendations.prioritized_actions) && recommendations.prioritized_actions.length > 0 && (
        <SectionCardAccordion title={t("prioritizedActions")} icon={<FaIcon icon="fa-bullseye" color={SECTION_ICON_COLOR} size={SECTION_ICON_SIZE} />}>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {recommendations.prioritized_actions.map((action, index) => (
              <CategorizedAnalysisRecsAndPrioritizedActionsCard key={action.id || index} item={action} index={index} />
            ))}
          </div>
        </SectionCardAccordion>
      )}

      {/* Section 4: Target */}
      {recommendations.target && (
        <SectionCardAccordion title={t("target")} icon={<FaIcon icon="fa-crosshairs" color={SECTION_ICON_COLOR} size={SECTION_ICON_SIZE} />}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "12px" }}>
            <StatChip label={t("provider")} value={recommendations.target.provider || null} color="#3b82f6" capitalize />
            <StatChip label={t("model")} value={recommendations.target.model || null} color="#8b5cf6" />
            <StatChip label={t("apiVersion")} value={recommendations.target.api_version || null} color="#0ea5e9" />
            <StatChip label={t("operation")} value={recommendations.target.operation || null} color="#10b981" capitalize />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px" }}>
            <StatChip label={t("baseUrl")} value={recommendations.target.base_url || null} color="#f59e0b" />
            <StatChip label={t("url")} value={recommendations.target.url || null} color="#ea580c" />
          </div>
        </SectionCardAccordion>
      )}

      {/* Section 5: Score Interpretation */}
      {recommendations.score_interpretation && (() => {
        const si = recommendations.score_interpretation;
        const formatStatValue = (value) => {
          if (typeof value === "boolean") return value ? "Yes" : "No";
          if (typeof value === "number") {
            return Number.isInteger(value) ? value : value.toFixed(2);
          }
          return value;
        };
        const metricCard = (label, value, color = "#3b82f6") => (
          <div style={{
            background: "rgba(107, 114, 128, 0.05)",
            borderRadius: "10px",
            padding: "16px",
            border: "1px solid rgba(107, 114, 128, 0.1)",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              {label}
            </div>
            <div style={{ fontSize: "20px", fontWeight: 700, color }}>
              {formatStatValue(value)}
            </div>
          </div>
        );

        return (
          <SectionCardAccordion title={t("scoreInterpretation")} icon={<FaIcon icon="fa-calculator" color={SECTION_ICON_COLOR} size={SECTION_ICON_SIZE} />}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "12px" }}>
              {si.declared_overall_readiness !== undefined && metricCard(t("declaredOverallReadiness"), si.declared_overall_readiness + "%", "#3b82f6")}
              {si.calculated_from_summary !== undefined && metricCard(t("calculatedFromSummary"), si.calculated_from_summary + "%", "#8b5cf6")}
              {si.readiness_difference !== undefined && metricCard(t("readinessDifference"), si.readiness_difference + "%", "#6b7280")}
              {si.total_plans !== undefined && metricCard(t("totalPlans"), si.total_plans, "#0ea5e9")}
              {si.readiness_based_risk_posture && (() => {
                const style = getRiskLevelStyle(si.readiness_based_risk_posture);
                return (
                  <div style={{
                    background: style.bg,
                    borderRadius: "10px",
                    padding: "16px",
                    border: `1px solid ${style.border}`,
                    textAlign: "center"
                  }}>
                    <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {t("readinessBasedRiskPosture")}

                    </div>
                    <div style={{ fontSize: "20px", fontWeight: 700, color: style.text, textTransform: "capitalize" }}>
                      {levelDisplayNames[si.readiness_based_risk_posture] || formatPostureDisplay(si.readiness_based_risk_posture)}
                    </div>
                  </div>
                );
              })()}
              {si.effective_risk_posture && (() => {
                const style = getRiskLevelStyle(si.effective_risk_posture);
                return (
                  <div style={{
                    background: style.bg,
                    borderRadius: "10px",
                    padding: "16px",
                    border: `1px solid ${style.border}`,
                    textAlign: "center"
                  }}>
                    <div style={{ fontSize: "11px", color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {t("effectiveRiskPosture")}

                    </div>
                    <div style={{ fontSize: "20px", fontWeight: 700, color: style.text, textTransform: "capitalize" }}>
                      {levelDisplayNames[si.effective_risk_posture] || formatPostureDisplay(si.effective_risk_posture)}
                    </div>
                  </div>
                );
              })()}
            </div>

            {si.summary_totals && (
              <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                  {t("summaryTotals")}

                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "12px" }}>
                  <StatChip label={t("totalCases")} value={si.summary_totals.total_cases !== undefined ? formatStatValue(si.summary_totals.total_cases) : null} color="#3b82f6" />
                  <StatChip label={t("totalPassed")} value={si.summary_totals.total_passed !== undefined ? formatStatValue(si.summary_totals.total_passed) : null} color="#22c55e" />
                  <StatChip label={t("totalFailed")} value={si.summary_totals.total_failed !== undefined ? formatStatValue(si.summary_totals.total_failed) : null} color="#ef4444" />
                  <StatChip label={t("totalBlocked")} value={si.summary_totals.total_blocked !== undefined ? formatStatValue(si.summary_totals.total_blocked) : null} color="#10b981" />
                  <StatChip label={t("totalAllowed")} value={si.summary_totals.total_allowed !== undefined ? formatStatValue(si.summary_totals.total_allowed) : null} color="#f59e0b" />
                  <StatChip
                    label={t("allTestsPassed")}
                    value={si.summary_totals.all_tests_passed !== undefined ? formatStatValue(si.summary_totals.all_tests_passed) : null}
                    color={si.summary_totals.all_tests_passed ? "#22c55e" : "#ef4444"}
                  />
                  <StatChip
                    label={t("duration")}
                    value={si.summary_totals.duration_seconds !== undefined ? `${formatStatValue(si.summary_totals.duration_seconds)}s` : null}
                    color="#8b5cf6"
                  />
                </div>
              </div>
            )}

            {si.enabled_categories && Array.isArray(si.enabled_categories) && si.enabled_categories.length > 0 && (
              <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                  {t("enabledCategories")}

                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {si.enabled_categories.map((category, idx) => (
                    <span key={idx} style={{
                      padding: "6px 12px",
                      borderRadius: "6px",
                      fontSize: "12px",
                      background: "rgba(139, 92, 246, 0.1)",
                      color: "#8b5cf6",
                      fontWeight: 500
                    }}>
                      {formatCategoryName(category)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {si.quick_stats && (
              <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                  {t("quickStats")}

                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "12px" }}>
                  <StatChip label={t("totalTests")} value={si.quick_stats.total_tests !== undefined ? formatStatValue(si.quick_stats.total_tests) : null} color="#3b82f6" />
                  <StatChip label={t("overallReadiness")} value={si.quick_stats.overall_readiness !== undefined ? formatStatValue(si.quick_stats.overall_readiness) + "%" : null} color="#22c55e" />
                  <StatChip label={t("totalFailedTests")} value={si.quick_stats.total_failed_tests !== undefined ? formatStatValue(si.quick_stats.total_failed_tests) : null} color="#ef4444" />
                  <StatChip label={t("totalHighRisk")} value={si.quick_stats.total_high_risk !== undefined ? formatStatValue(si.quick_stats.total_high_risk) : null} color="#ea580c" />
                  <StatChip label={t("totalCategoriesTested")} value={si.quick_stats.total_categories_tested !== undefined ? formatStatValue(si.quick_stats.total_categories_tested) : null} color="#8b5cf6" />
                  <StatChip label={t("overallAvgResponseTime")} value={si.quick_stats.overall_avg_response_time !== undefined ? formatStatValue(si.quick_stats.overall_avg_response_time) : null} color="#f59e0b" />
                  <StatChip
                    label={t("overallRiskLevel")}
                    value={si.quick_stats.overall_risk_level
                      ? (levelDisplayNames[si.quick_stats.overall_risk_level] || formatStatValue(si.quick_stats.overall_risk_level))
                      : null}
                    color={si.quick_stats.overall_risk_level ? getRiskLevelColor(si.quick_stats.overall_risk_level) : "#3b82f6"}
                    capitalize
                  />
                </div>
              </div>
            )}

            {si.data_readiness?.llm_api_mis_use_abuse && (() => {
              const group = si.data_readiness.llm_api_mis_use_abuse;
              return (
                <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                    {t("dataReadiness")}

                  </div>
                  <div style={{
                    background: "rgba(107, 114, 128, 0.05)",
                    borderRadius: "12px",
                    padding: "16px",
                    border: "1px solid rgba(107, 114, 128, 0.1)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px"
                  }}>
                    <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                      {formatCategoryName("llm_api_mis_use_abuse")}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: "10px" }}>
                      <StatChip label={t("readiness")} value={group.readiness !== undefined ? formatStatValue(group.readiness) + "%" : null} color="#22c55e" />
                      <StatChip label={t("totalTests")} value={group.total_tests !== undefined ? formatStatValue(group.total_tests) : null} color="#3b82f6" />
                      <StatChip label={t("totalPassed")} value={group.total_passed !== undefined ? formatStatValue(group.total_passed) : null} color="#10b981" />
                      <StatChip label={t("totalFailed")} value={group.total_failed !== undefined ? formatStatValue(group.total_failed) : null} color="#ef4444" />
                      <StatChip label={t("avgResponseTime")} value={group.avg_response_time !== undefined ? formatStatValue(group.avg_response_time) : null} color="#f59e0b" />
                    </div>
                    {group.sub_cat_readiness && (
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", textTransform: "uppercase" }}>
                          {t("subCategoryReadiness")}

                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "8px" }}>
                          {Object.entries(group.sub_cat_readiness).map(([cat, readiness]) => (
                            <StatChip
                              key={cat}
                              label={formatCategoryName(cat)}
                              value={readiness !== undefined ? formatStatValue(readiness) + "%" : null}
                              color="#3b82f6"
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </SectionCardAccordion>
        );
      })()}

      {/* Section 6: Implementation */}
      {((recommendations.implementation_sequence && Array.isArray(recommendations.implementation_sequence) && recommendations.implementation_sequence.length > 0) ||
        (recommendations.implementation_notes && Array.isArray(recommendations.implementation_notes) && recommendations.implementation_notes.length > 0)) && (
        <SectionCardAccordion title={t("implementation")} icon={<FaIcon icon="fa-list-ol" color={SECTION_ICON_COLOR} size={SECTION_ICON_SIZE} />}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" }}>
            {recommendations.implementation_sequence && Array.isArray(recommendations.implementation_sequence) && recommendations.implementation_sequence.length > 0 && (
              <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                  {t("sequence")}

                </div>
                <div style={{
                  background: "rgba(107, 114, 128, 0.05)",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "1px solid rgba(107, 114, 128, 0.1)",
                  flex: 1
                }}>
                  <ol style={{ margin: 0, paddingLeft: "20px", color: "var(--body-text)", fontSize: "13px", lineHeight: 1.8 }}>
                    {recommendations.implementation_sequence.map((step, idx) => (
                      <li key={idx}>{step}</li>
                    ))}
                  </ol>
                </div>
              </div>
            )}
            {recommendations.implementation_notes && Array.isArray(recommendations.implementation_notes) && recommendations.implementation_notes.length > 0 && (
              <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                  {t("notes")}

                </div>
                <div style={{
                  background: "rgba(107, 114, 128, 0.05)",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "1px solid rgba(107, 114, 128, 0.1)",
                  flex: 1
                }}>
                  <ol style={{ margin: 0, paddingLeft: "20px", color: "var(--body-text)", fontSize: "13px", lineHeight: 1.8 }}>
                    {recommendations.implementation_notes.map((note, idx) => (
                      <li key={idx}>{note}</li>
                    ))}
                  </ol>
                </div>
              </div>
            )}
          </div>
        </SectionCardAccordion>
      )}

      {/* Section 7: WAF Rules */}
      {recommendations.waf_rules && (
        <SectionCardAccordion title={t("wafRules")} icon={<FaIcon icon="fa-shield-alt" color={SECTION_ICON_COLOR} size={SECTION_ICON_SIZE} />}>
          {recommendations.waf_rules.rule_sets && Object.keys(recommendations.waf_rules.rule_sets).length > 0 && (
            <div style={{ marginBottom: "24px" }}>
              <h4 style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "600", color: "var(--card-text)" }}>
                {t("ruleSets")}

              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                {Object.entries(recommendations.waf_rules.rule_sets).map(([ruleSetName, ruleSet]) => (
                  <div key={ruleSetName} style={{
                    border: "1px solid var(--card-border)",
                    borderRadius: "12px",
                    padding: "20px",
                    background: "var(--card-bg)"
                  }}>
                    <div style={{ fontSize: "15px", fontWeight: 600, color: "var(--card-text)", marginBottom: "8px", textTransform: "capitalize" }}>
                      {formatCategoryName(ruleSetName)}
                    </div>
                    {ruleSet.description && (
                      <div style={{ fontSize: "13px", color: "var(--muted-text)", marginBottom: "16px" }}>
                        {ruleSet.description}
                      </div>
                    )}
                    {ruleSet.rules && Array.isArray(ruleSet.rules) && ruleSet.rules.length > 0 && (
                      <div style={{ overflowX: "auto", border: "1px solid var(--card-border)", borderRadius: "8px" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                          <thead>
                            <tr style={{ background: "rgba(107, 114, 128, 0.06)" }}>
                              <th style={{ textAlign: "left", padding: "10px 12px", color: "var(--muted-text)", fontWeight: 600, textTransform: "uppercase", fontSize: "11px", borderBottom: "1px solid var(--card-border)" }}>{t("name")}</th>
                              <th style={{ textAlign: "left", padding: "10px 12px", color: "var(--muted-text)", fontWeight: 600, textTransform: "uppercase", fontSize: "11px", borderBottom: "1px solid var(--card-border)" }}>{t("action")}</th>
                              <th style={{ textAlign: "left", padding: "10px 12px", color: "var(--muted-text)", fontWeight: 600, textTransform: "uppercase", fontSize: "11px", borderBottom: "1px solid var(--card-border)" }}>{t("condition")}</th>
                              <th style={{ textAlign: "left", padding: "10px 12px", color: "var(--muted-text)", fontWeight: 600, textTransform: "uppercase", fontSize: "11px", borderBottom: "1px solid var(--card-border)" }}>{t("response")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ruleSet.rules.map((rule, index) => (
                              <tr key={index} style={{ borderBottom: index < ruleSet.rules.length - 1 ? "1px solid var(--card-border)" : "none" }}>
                                <td style={{ padding: "12px", color: "var(--card-text)", fontWeight: 600, fontFamily: "monospace", verticalAlign: "top" }}>
                                  {rule.name}
                                </td>
                                <td style={{ padding: "12px", verticalAlign: "top" }}>
                                  <span style={{
                                    padding: "4px 10px",
                                    borderRadius: "999px",
                                    fontSize: "10px",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    color: rule.action === "BLOCK" || rule.action === "block" ? "#ef4444" : "#10b981",
                                    background: rule.action === "BLOCK" || rule.action === "block" ? "rgba(239, 68, 68, 0.1)" : "rgba(16, 185, 129, 0.1)",
                                    whiteSpace: "nowrap"
                                  }}>
                                    {rule.action}
                                  </span>
                                </td>
                                <td style={{ padding: "12px", color: "var(--body-text)", lineHeight: 1.5, verticalAlign: "top" }}>
                                  {rule.condition}
                                </td>
                                <td style={{ padding: "12px", color: "var(--body-text)", lineHeight: 1.5, verticalAlign: "top" }}>
                                  {rule.response}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {recommendations.waf_rules.custom_rules && Array.isArray(recommendations.waf_rules.custom_rules) && recommendations.waf_rules.custom_rules.length > 0 && (
            <div style={{ marginBottom: "24px" }}>
              <h4 style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "600", color: "var(--card-text)" }}>
                {t("customRules")}

              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px" }}>
                {recommendations.waf_rules.custom_rules.map((rule, index) => (
                  <div key={index} style={{
                    border: "1px solid var(--card-border)",
                    borderRadius: "8px",
                    padding: "16px",
                    background: "rgba(59, 130, 246, 0.03)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    minWidth: 0,
                    overflow: "hidden"
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--card-text)", marginBottom: "8px", fontFamily: "monospace" }}>
                          {rule.name}
                        </div>
                      </div>
                      <span style={{
                        padding: "4px 10px",
                        borderRadius: "999px",
                        fontSize: "10px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        color: rule.action === "BLOCK" || rule.action === "block" ? "#ef4444" : "#10b981",
                        background: rule.action === "BLOCK" || rule.action === "block" ? "rgba(239, 68, 68, 0.1)" : "rgba(16, 185, 129, 0.1)",
                        whiteSpace: "nowrap"
                      }}>
                        {rule.action}
                      </span>
                    </div>
                    {rule.description && (
                      <div style={{ fontSize: "13px", color: "var(--body-text)", lineHeight: 1.5 }}>
                        {rule.description}
                      </div>
                    )}
                    {rule.pattern && (
                      <div style={{ marginTop: "4px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "6px", textTransform: "uppercase" }}>
                          {t("pattern")}

                        </div>
                        <pre style={codeCompactStyle}>
                          {rule.pattern}
                        </pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {recommendations.waf_rules.deployment_guide && (
            <div>
              <h4 style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "600", color: "var(--card-text)" }}>
                {t("deploymentGuide")}

              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {Object.entries(recommendations.waf_rules.deployment_guide).map(([platform, guide]) => (
                  <div key={platform} style={{
                    background: "rgba(59, 130, 246, 0.05)",
                    borderRadius: "12px",
                    padding: "20px",
                    border: "1px solid rgba(59, 130, 246, 0.15)",
                    minWidth: 0,
                    overflow: "hidden"
                  }}>
                    <div style={{ fontSize: "15px", fontWeight: 600, color: "var(--card-text)", marginBottom: "16px", textTransform: "uppercase" }}>
                      {formatCategoryName(platform)}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" }}>
                      {guide.recommended_settings && Object.keys(guide.recommended_settings).length > 0 && (
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", marginBottom: "12px" }}>
                            {t("recommendedSettings")}

                          </div>
                          <div style={{
                            background: "rgba(107, 114, 128, 0.05)",
                            padding: "16px",
                            borderRadius: "8px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                            minWidth: 0
                          }}>
                            {Object.entries(guide.recommended_settings).map(([key, value]) => (
                              <div key={key} style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: "16px",
                                paddingBottom: "10px",
                                borderBottom: "1px solid rgba(107, 114, 128, 0.1)",
                                minWidth: 0
                              }}>
                                <strong style={{
                                  color: "var(--card-text)",
                                  fontSize: "13px",
                                  textTransform: "capitalize",
                                  fontWeight: 500,
                                  flexShrink: 0
                                }}>
                                  {key.replace(/_/g, " ")}
                                </strong>
                                <span style={{
                                  color: "var(--body-text)",
                                  fontSize: "13px",
                                  textAlign: "right",
                                  flex: 1,
                                  minWidth: 0,
                                  maxWidth: "60%",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word"
                                }}>
                                  {typeof value === "boolean" ? (value ? "true" : "false") : String(value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {guide.rollout && Array.isArray(guide.rollout) && guide.rollout.length > 0 && (
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", marginBottom: "12px" }}>
                            {t("rollout")}

                          </div>
                          <div style={{
                            background: "rgba(107, 114, 128, 0.05)",
                            padding: "16px",
                            borderRadius: "8px"
                          }}>
                            <ol style={{ margin: 0, paddingLeft: "20px", color: "var(--body-text)", fontSize: "13px", lineHeight: 1.8 }}>
                              {guide.rollout.map((step, idx) => (
                                <li key={idx}>{step}</li>
                              ))}
                            </ol>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {recommendations.waf_rules.cloud_specific_rules && Object.keys(recommendations.waf_rules.cloud_specific_rules).length > 0 && (
            <div style={{ marginTop: "24px" }}>
              <h4 style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "600", color: "var(--card-text)" }}>
                {t("cloudSpecificRules")}

              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" }}>
                {Object.entries(recommendations.waf_rules.cloud_specific_rules).map(([cloud, config]) => (
                  <div key={cloud} style={{
                    background: "rgba(107, 114, 128, 0.05)",
                    borderRadius: "12px",
                    padding: "16px",
                    border: "1px solid rgba(107, 114, 128, 0.1)",
                    minWidth: 0,
                    overflow: "hidden"
                  }}>
                    <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--card-text)", marginBottom: "12px", textTransform: "capitalize" }}>
                      {formatCategoryName(cloud)}
                    </div>
                    <pre style={codeCompactStyle}>
                      {JSON.stringify(config, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          )}

          {recommendations.waf_rules.important_limitations && Array.isArray(recommendations.waf_rules.important_limitations) && recommendations.waf_rules.important_limitations.length > 0 && (
            <div style={{ marginTop: "24px" }}>
              <h4 style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "600", color: "var(--card-text)" }}>
                {t("importantLimitations")}

              </h4>
              <div style={{
                background: "rgba(107, 114, 128, 0.05)",
                padding: "16px",
                borderRadius: "8px",
                border: "1px solid rgba(107, 114, 128, 0.1)"
              }}>
                <ul style={{ margin: 0, paddingLeft: "20px", color: "var(--body-text)", fontSize: "13px", lineHeight: 1.8 }}>
                  {recommendations.waf_rules.important_limitations.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </SectionCardAccordion>
      )}

      {/* Section 8: Python Security Implementation */}
      {recommendations.python_security_implementation && (() => {
        const psi = recommendations.python_security_implementation;

        return (
          <SectionCardAccordion
            title={t("pythonSecurityRecommendations")}
            icon={<FaIcon icon="fa-terminal" color={SECTION_ICON_COLOR} size={SECTION_ICON_SIZE} />}
          >
            {(psi.title || psi.purpose) && (
              <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                  {t("overview")}

                </div>
                <div style={{
                  background: "rgba(107, 114, 128, 0.05)",
                  borderRadius: "8px",
                  border: "1px solid rgba(107, 114, 128, 0.1)",
                  padding: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px"
                }}>
                  {psi.title && (
                    <div>
                      <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "4px", textTransform: "uppercase" }}>
                        {t("title")}

                      </div>
                      <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--card-text)", lineHeight: 1.5 }}>
                        {psi.title}
                      </div>
                    </div>
                  )}
                  {psi.purpose && (
                    <div>
                      <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "4px", textTransform: "uppercase" }}>
                        {t("purpose")}

                      </div>
                      <div style={{ fontSize: "14px", color: "var(--body-text)", lineHeight: 1.6 }}>
                        {psi.purpose}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {((psi.deployment_checklist && Array.isArray(psi.deployment_checklist) && psi.deployment_checklist.length > 0) ||
              (psi.limitations && Array.isArray(psi.limitations) && psi.limitations.length > 0) ||
              (psi.integration_order && Array.isArray(psi.integration_order) && psi.integration_order.length > 0)) && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "16px" }}>
                  {psi.deployment_checklist && Array.isArray(psi.deployment_checklist) && psi.deployment_checklist.length > 0 && (
                    <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                      <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                        {t("deploymentChecklist")}

                      </div>
                      <div style={{
                        background: "rgba(107, 114, 128, 0.05)",
                        padding: "16px",
                        borderRadius: "8px",
                        border: "1px solid rgba(107, 114, 128, 0.1)",
                        flex: 1
                      }}>
                        <ul style={{ margin: 0, paddingLeft: "20px", color: "var(--body-text)", fontSize: "13px", lineHeight: 1.8 }}>
                          {psi.deployment_checklist.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                  {psi.limitations && Array.isArray(psi.limitations) && psi.limitations.length > 0 && (
                    <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                      <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                        {t("limitations")}

                      </div>
                      <div style={{
                        background: "rgba(107, 114, 128, 0.05)",
                        padding: "16px",
                        borderRadius: "8px",
                        border: "1px solid rgba(107, 114, 128, 0.1)",
                        flex: 1
                      }}>
                        <ul style={{ margin: 0, paddingLeft: "20px", color: "var(--body-text)", fontSize: "13px", lineHeight: 1.8 }}>
                          {psi.limitations.map((limitation, idx) => (
                            <li key={idx}>{limitation}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                  {psi.integration_order && Array.isArray(psi.integration_order) && psi.integration_order.length > 0 && (
                    <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                      <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                        {t("integrationOrder")}

                      </div>
                      <div style={{
                        background: "rgba(107, 114, 128, 0.05)",
                        padding: "16px",
                        borderRadius: "8px",
                        border: "1px solid rgba(107, 114, 128, 0.1)",
                        flex: 1
                      }}>
                        <ol style={{ margin: 0, paddingLeft: "20px", color: "var(--body-text)", fontSize: "13px", lineHeight: 1.8 }}>
                          {psi.integration_order.map((step, idx) => (
                            <li key={idx}>{step}</li>
                          ))}
                        </ol>
                      </div>
                    </div>
                  )}
                </div>
              )}

            {psi.runtime_requirements && Array.isArray(psi.runtime_requirements) && psi.runtime_requirements.length > 0 && (
              <div>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", marginBottom: "10px", textTransform: "uppercase" }}>
                  {t("runtimeRequirements")}

                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {psi.runtime_requirements.map((req, idx) => (
                    <span key={idx} style={{
                      padding: "6px 12px",
                      borderRadius: "8px",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#3b82f6",
                      background: "rgba(59, 130, 246, 0.1)",
                      border: "1px solid rgba(59, 130, 246, 0.15)"
                    }}>
                      {req}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {psi.installation && Array.isArray(psi.installation) && psi.installation.length > 0 && (
              <div>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", marginBottom: "12px", textTransform: "uppercase" }}>
                  {t("installation")}

                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {psi.installation.map((cmd, idx) => (
                    <pre key={idx} style={codeCompactStyle}>
                      {cmd}
                    </pre>
                  ))}
                </div>
              </div>
            )}

            {psi.code_snippet && (
              <div>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", marginBottom: "12px", textTransform: "uppercase" }}>
                  {t("codeSnippet")}

                </div>
                <pre style={codeSnippetStyle}>
                  {psi.code_snippet.trim()}
                </pre>
              </div>
            )}

            {psi.code_examples && typeof psi.code_examples === "object" && Object.keys(psi.code_examples).length > 0 && (
              <div>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", marginBottom: "12px", textTransform: "uppercase" }}>
                  {t("codeExamples")}

                </div>
                <div style={{
                  background: "rgba(107, 114, 128, 0.05)",
                  borderRadius: "8px",
                  border: "1px solid rgba(107, 114, 128, 0.1)",
                  padding: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px"
                }}>
                  {Object.entries(psi.code_examples).map(([name, code]) => (
                    <div key={name} style={{ minWidth: 0 }}>
                      <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px", textTransform: "uppercase" }}>
                        {formatCategoryName(name)}
                      </div>
                      <pre style={codeSnippetStyle}>
                        {typeof code === "string" ? code.trim() : String(code)}
                      </pre>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {psi.uvicorn_production_example && (
              <div>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", marginBottom: "12px", textTransform: "uppercase" }}>
                  {t("uvicornProductionExample")}

                </div>
                <div style={{
                  background: "rgba(107, 114, 128, 0.05)",
                  borderRadius: "8px",
                  border: "1px solid rgba(107, 114, 128, 0.1)",
                  padding: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px"
                }}>
                  {psi.uvicorn_production_example.command && (
                    <div>
                      <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px", textTransform: "uppercase" }}>
                        {t("command")}

                      </div>
                      <pre style={codeCompactStyle}>
                        {psi.uvicorn_production_example.command}
                      </pre>
                    </div>
                  )}
                  {psi.uvicorn_production_example.tuning && Array.isArray(psi.uvicorn_production_example.tuning) && psi.uvicorn_production_example.tuning.length > 0 && (
                    <div>
                      <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--muted-text)", marginBottom: "8px", textTransform: "uppercase" }}>
                        {t("tuning")}

                      </div>
                      <div style={{
                        background: "rgba(107, 114, 128, 0.05)",
                        padding: "16px",
                        borderRadius: "8px",
                        border: "1px solid rgba(107, 114, 128, 0.1)"
                      }}>
                        <ul style={{ margin: 0, paddingLeft: "20px", color: "var(--body-text)", fontSize: "13px", lineHeight: 1.8 }}>
                          {psi.uvicorn_production_example.tuning.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </SectionCardAccordion>
        );
      })()}

      {/* Section 9: Metadata */}
      {recommendations.metadata && (() => {
        const md = recommendations.metadata;
        return (
          <SectionCardAccordion title={t("metadata")} icon={<FaIcon icon="fa-file-alt" color={SECTION_ICON_COLOR} size={SECTION_ICON_SIZE} />}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "12px" }}>
              <StatChip
                label={t("generatedAt")}
                value={md.generated_at ? new Date(md.generated_at).toLocaleString() : null}
                color="#3b82f6"
              />
              <StatChip label={t("generatorName")} value={md.generator_name || null} color="#8b5cf6" />
              <StatChip label={t("generatorVersion")} value={md.generator_version || null} color="#0ea5e9" />
              <StatChip label={t("inputWrapper")} value={md.input_wrapper || null} color="#10b981" />
              <StatChip label={t("reportId")} value={md.report_id != null ? md.report_id : null} color="#f59e0b" />
              <StatChip label={t("profileId")} value={md.profile_id != null ? md.profile_id : null} color="#ea580c" />
              <StatChip label={t("reportName")} value={md.report_name || null} color="#6366f1" />
              <StatChip
                label={t("recommendationsGenerated")}
                value={md.recommendations_generated != null ? md.recommendations_generated : null}
                color="#22c55e"
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px" }}>
              <StatChip
                label={t("sourceCreatedAt")}
                value={md.source_created_at ? new Date(md.source_created_at).toLocaleString() : null}
                color="#64748b"
              />
              <StatChip label={t("targetUrl")} value={md.target_url || null} color="#ef4444" />
            </div>
            {md.categories_analyzed && Array.isArray(md.categories_analyzed) && md.categories_analyzed.length > 0 && (
              <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--card-text)", textTransform: "uppercase" }}>
                  {t("categoriesAnalyzed")}

                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {md.categories_analyzed.map((category, idx) => (
                    <span key={idx} style={{
                      padding: "6px 12px",
                      borderRadius: "6px",
                      fontSize: "12px",
                      background: "rgba(139, 92, 246, 0.1)",
                      color: "#8b5cf6",
                      fontWeight: 500
                    }}>
                      {formatCategoryName(category)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </SectionCardAccordion>
        );
      })()}

    </div>
  );
};

export default AiModelAttackSurfaceRecs;
